import { SectionPreviewPane } from '@/components/common/section-preview-pane';
import { ZyPreviewButton } from '@/components/common/zypreview-button';
import { AppPageLayout } from '@/components/layout/app-page-layout';
import { GlobalTopBar } from '@/components/layout/global-top-bar';
import { parseSandboxContentFile } from '@/components/sandbox-editor/parse-sandbox-content';
import { TiptapEditor, type TiptapOnCreateCallbackProps } from '@/components/tiptap-editor/tiptap-editor';
import type { TableOfContentData } from '@/components/tiptap-editor/tiptap-extension/zy-table-of-contents-extension';
import { useZySectionLivePreview } from '@/hooks/use-zy-section-live-preview';
import { setAllImagesRenderAsHTML, setDocAttributes } from '@/lib/zy-tiptap-utils';
import { sectionToMigratedProseMirrorJson } from '@/lib/zybooks-parser/section-to-migrated-prosemirror';
import { mapTableOfContentsToHeadingOutline, type HeadingOutlineItem } from '@/nav/heading-outline';
import { getSandboxDemos } from '@/nav/sandbox-demos';
import { usePlatformSidebarOutlet } from '@/providers/platform-sidebar-outlet';
import { Editor, type Content } from '@tiptap/react';
import { LoaderCircle } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
interface SandboxEditorForDemoProps {
    demoId: string;
    demoName: string;
    content: Content;
}

const contentModules = import.meta.glob('@/tests/sandbox-content/*.json', { eager: true });

function getRawJsonForDemo(contentPath: string): unknown {
    const matchingKey = Object.keys(contentModules).find(key => key.endsWith(contentPath));
    if (matchingKey && contentModules[matchingKey]) {
        return (contentModules[matchingKey] as { default: unknown }).default;
    }
    return null;
}

function missingFileContent(contentPath: string): Content {
    return {
        type: 'doc',
        content: [
            {
                type: 'paragraph',
                content: [{ type: 'text', text: `Content file not found: ${contentPath}` }],
            },
        ],
    };
}

export function SandboxEditor() {
    const { demoId } = useParams<{ demoId: string }>();
    const [content, setContent] = useState<Content | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [prevDemoId, setPrevDemoId] = useState(demoId);

    const navigate = useNavigate();
    const { resetDemoNav } = usePlatformSidebarOutlet();

    if (demoId !== prevDemoId) {
        setPrevDemoId(demoId);
        setIsLoading(true);
        setLoadError(null);
        setContent(null);
    }

    // Auto-redirect to first demo if no demo is selected
    useEffect(() => {
        if (!demoId) {
            const list = getSandboxDemos();
            if (list.length > 0) {
                navigate(`/demo/${list[0].id}`, { replace: true });
            }
        }
    }, [demoId, navigate]);

    const currentDemo = useMemo(() => getSandboxDemos().find(d => d.id === demoId), [demoId]);

    useEffect(() => {
        if (!currentDemo) return;

        let cancelled = false;
        resetDemoNav();

        (async () => {
            try {
                const raw = getRawJsonForDemo(currentDemo.contentPath);
                if (raw === null) {
                    if (!cancelled) {
                        setContent(missingFileContent(currentDemo.contentPath));
                        setIsLoading(false);
                    }
                    return;
                }

                const parsed = parseSandboxContentFile(raw);
                const pm = await sectionToMigratedProseMirrorJson(parsed.section);
                if (!cancelled) {
                    setContent(pm);
                    setIsLoading(false);
                }
            } catch (err) {
                console.error('Failed to load sandbox content', err);
                if (!cancelled) {
                    setLoadError(err instanceof Error ? err.message : 'Failed to load sandbox content');
                    setContent(null);
                    setIsLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [currentDemo, resetDemoNav]);

    if (!demoId) {
        return null;
    }

    if (!currentDemo) {
        return (
            <div className="flex h-full items-center justify-center">
                <div className="text-center">
                    <h2 className="text-lg font-semibold text-gray-900">Demo not found</h2>
                    <p className="mt-1 text-sm text-gray-500">
                        The demo "{demoId}" does not exist. Please select a valid demo from the sidebar.
                    </p>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="flex h-full items-center justify-center p-4">
                <div className="max-w-lg text-center text-sm text-red-700">{loadError}</div>
            </div>
        );
    }

    if (isLoading || !content) {
        return (
            <div className="flex h-full items-center justify-center">
                <LoaderCircle className="h-8 w-8 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <SandboxEditorForDemo
            key={demoId}
            demoId={currentDemo.id}
            demoName={currentDemo.name}
            content={content}
        />
    );
}

/**
 * Owns the editor + live-preview lifecycle for a single demo.
 */
function SandboxEditorForDemo({ demoId, demoName, content }: SandboxEditorForDemoProps) {
    const [editor, setEditor] = useState<Editor | null>(null);
    const editorRef = useRef<Editor | null>(null);
    const editorScrollRef = useRef<HTMLDivElement>(null);
    const outlineSnapshotRef = useRef('');
    const headingsRef = useRef<HeadingOutlineItem[]>([]);
    const mountedRef = useRef(true);
    const registrationKey = useId();
    const { registerDemoNav, clearDemoNav } = usePlatformSidebarOutlet();

    const { iframeRef, iframeURL, connectionError, tiptapEditorRef, previewMode, previewError, isRenderingOutput, togglePreviewMode } =
        useZySectionLivePreview(editor, { prepareEditor: setAllImagesRenderAsHTML });

    const handleTableOfContentsUpdate = useCallback(
        (anchors: TableOfContentData) => {
            if (!mountedRef.current) return;

            const activeEditor = editorRef.current;
            if (!activeEditor) return;

            const headings = mapTableOfContentsToHeadingOutline(anchors);
            headingsRef.current = headings;
            const json = JSON.stringify(headings.map((h: HeadingOutlineItem) => [h.id, h.title, h.level]));
            if (json === outlineSnapshotRef.current) return;
            outlineSnapshotRef.current = json;

            registerDemoNav({
                key: registrationKey,
                demoId,
                headings,
                scrollToHeading: (headingId, index) => {
                    const heading =
                        (headingId != null ? headingsRef.current.find(h => h.id === headingId) : null) ?? headingsRef.current[index];
                    if (!heading) return;
                    activeEditor.commands.scrollToPos(heading.pos);
                },
            });
        },
        [demoId, registerDemoNav, registrationKey],
    );

    const extensionOptions = useMemo(
        () => ({
            tableOfContents: {
                onUpdate: handleTableOfContentsUpdate,
            },
        }),
        [handleTableOfContentsUpdate],
    );

    const handleTiptapCreate = ({ editor: createdEditor }: TiptapOnCreateCallbackProps) => {
        setDocAttributes(createdEditor, {
            baseImageUrl: 'https://zytools.zybooks.com/zyAuthor/MATEST/MATEST/IMAGES/',

            chapterNumber: 1,
            sectionId: '2a826cdc-1e15-17d4-66c3-271c3a926fb5',
            sectionNumber: 1,
            releaseId: 'MATEST',
            subjectName: 'MATEST',
            uploadImagesToMATESTBucket: true,
        });

        editorRef.current = createdEditor;
        createdEditor.commands.setContentWithMeta(content);
        outlineSnapshotRef.current = '';
        setEditor(createdEditor);
    };

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            editorRef.current = null;
            clearDemoNav(registrationKey);
        };
    }, [clearDemoNav, registrationKey]);

    useEffect(() => {
        outlineSnapshotRef.current = '';
    }, [content, demoId]);

    return (
        <AppPageLayout className="flex h-[calc(100svh-49px)] flex-col min-h-0 min-w-0">
            <GlobalTopBar.Start>
                <h1 className="text-lg font-semibold">{demoName}</h1>
            </GlobalTopBar.Start>
            <GlobalTopBar.Actions>
                <ZyPreviewButton
                    editor={editor}
                    previewMode={previewMode}
                    disabled={isRenderingOutput}
                    onTogglePreview={togglePreviewMode}
                />
            </GlobalTopBar.Actions>
            <AppPageLayout.Body className="min-w-0 flex-1 overflow-auto">
                <div
                    ref={editorScrollRef}
                    className="min-h-0 flex-1 overflow-auto"
                >
                    <SectionPreviewPane
                        visible={previewMode}
                        iframeRef={iframeRef}
                        iframeURL={iframeURL}
                        previewError={previewError}
                        connectionError={connectionError}
                    />
                    <div
                        className={`h-full w-full min-w-0 ${previewMode ? 'hidden' : ''}`}
                        aria-hidden={previewMode}
                    >
                        <TiptapEditor
                            ref={tiptapEditorRef}
                            content={content}
                            useCollaboration={false}
                            extensionOptions={extensionOptions}
                            onCreate={handleTiptapCreate}
                        />
                    </div>
                </div>
            </AppPageLayout.Body>
        </AppPageLayout>
    );
}
